import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  CalendarIcon,
  UserIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  PackageIcon,
  ScissorsIcon,
  CoinsIcon,
  CheckCircleIcon,
  Trash2Icon,
  AlertCircleIcon,
  Settings2Icon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PercentIcon,
  PencilIcon,
  RotateCcwIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";

const fmt = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function normalizeServiceSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function firstDayStr() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function AdminCommissions() {
  const [startDate, setStartDate] = useState(firstDayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [selectedProfId, setSelectedProfId] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searched, setSearched] = useState(false);

  // Modal de ajuste (desconto ou bônus)
  const [adjModal, setAdjModal] = useState<{ type: "discount" | "bonus" } | null>(null);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjDescription, setAdjDescription] = useState("");

  // Modal de confirmar pagamento
  const [payModal, setPayModal] = useState(false);

  // Painel de Regras de Comissão
  const [rulesOpen, setRulesOpen] = useState(false);
  const [ruleProf, setRuleProf] = useState<string>("");
  const [ruleServiceIds, setRuleServiceIds] = useState<number[]>([]);
  const [ruleServiceSearch, setRuleServiceSearch] = useState("");
  const [rulePercentage, setRulePercentage] = useState<string>("");
  const [ruleType, setRuleType] = useState<"professional" | "assistant">("professional");
  const [ruleFixedSalary, setRuleFixedSalary] = useState<string>("");

  // Modal de edição de regra existente
  const [editRuleModal, setEditRuleModal] = useState<any | null>(null);
  const [editRulePercentage, setEditRulePercentage] = useState("");
  const [editRuleFixedSalary, setEditRuleFixedSalary] = useState("");
  const [editRuleType, setEditRuleType] = useState<"professional" | "assistant">("professional");
  const [editRuleService, setEditRuleService] = useState<string>("all");

  const utils = trpc.useUtils();

  const { data: professionals } = trpc.professionals.listAdmin.useQuery();
  const { data: adminServices } = trpc.services.listAdmin.useQuery();
  const filteredRuleServices = useMemo(() => {
    const search = normalizeServiceSearch(ruleServiceSearch.trim());
    if (!search) return adminServices ?? [];
    return (adminServices ?? []).filter((service: any) =>
      normalizeServiceSearch(service.name).includes(search)
    );
  }, [adminServices, ruleServiceSearch]);

  // Queries e mutations de regras de comissão
  const { data: commissionRules, refetch: refetchRules } = trpc.commissions.listRules.useQuery();

  const upsertRule = trpc.commissions.upsertRule.useMutation({
    onSuccess: () => {
      toast.success("Regra salva com sucesso!");
      setRuleProf("");
      setRuleServiceIds([]);
      setRuleServiceSearch("");
      setRulePercentage("");
      setRuleType("professional");
      setRuleFixedSalary("");
      void refetchRules();
    },
    onError: (e) => toast.error(e.message),
  });

  const upsertRulesForServices = trpc.commissions.upsertRulesForServices.useMutation({
    onSuccess: ({ affectedCount }) => {
      toast.success(
        affectedCount === 1
          ? "Regra salva para 1 serviço."
          : `Regra salva para ${affectedCount} serviços.`
      );
      setRuleProf("");
      setRuleServiceIds([]);
      setRuleServiceSearch("");
      setRulePercentage("");
      setRuleType("professional");
      setRuleFixedSalary("");
      void refetchRules();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteRule = trpc.commissions.deleteRule.useMutation({
    onSuccess: () => {
      toast.success("Regra removida.");
      void refetchRules();
    },
    onError: (e) => toast.error(e.message),
  });

  function handleSaveRule() {
    if (!ruleProf) { toast.error("Selecione um profissional."); return; }
    const pct = parseFloat(rulePercentage.replace(",", "."));
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentual inválido (0-100)."); return; }
    const fixedSalaryVal = ruleFixedSalary.trim() ? parseFloat(ruleFixedSalary.replace(",", ".")) : null;
    if (fixedSalaryVal != null && (isNaN(fixedSalaryVal) || fixedSalaryVal < 0)) {
      toast.error("Salário fixo inválido.");
      return;
    }
    if (ruleType === "professional" && ruleServiceIds.length > 0) {
      upsertRulesForServices.mutate({
        professionalId: parseInt(ruleProf),
        serviceIds: ruleServiceIds,
        percentage: pct,
      });
      return;
    }
    upsertRule.mutate({
      professionalId: parseInt(ruleProf),
      serviceId: null,
      percentage: pct,
      ruleType,
      fixedMonthlySalary: fixedSalaryVal,
    });
  }

  function toggleRuleService(serviceId: number) {
    setRuleFixedSalary("");
    setRuleServiceIds((current) => current.includes(serviceId)
      ? current.filter((id) => id !== serviceId)
      : [...current, serviceId]);
  }

  function openEditRule(rule: any) {
    setEditRuleModal(rule);
    setEditRulePercentage(String(parseFloat(rule.percentage)));
    setEditRuleFixedSalary(rule.fixedMonthlySalary != null && Number(rule.fixedMonthlySalary) > 0 ? String(Number(rule.fixedMonthlySalary)) : "");
    setEditRuleType(rule.ruleType ?? "professional");
    setEditRuleService(rule.serviceId ? String(rule.serviceId) : "all");
  }

  function handleUpdateRule() {
    if (!editRuleModal) return;
    const pct = parseFloat(editRulePercentage.replace(",", "."));
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Percentual inválido (0-100)."); return; }
    const fixedSalaryVal = editRuleFixedSalary.trim() ? parseFloat(editRuleFixedSalary.replace(",", ".")) : null;
    upsertRule.mutate({
      professionalId: editRuleModal.professionalId,
      serviceId: editRuleType === "assistant" ? null : (editRuleService !== "all" ? parseInt(editRuleService) : null),
      percentage: pct,
      ruleType: editRuleType,
      fixedMonthlySalary: fixedSalaryVal,
    });
    setEditRuleModal(null);
  }

  const reportInput = useMemo(() => ({
    professionalId: selectedProfId !== "all" ? parseInt(selectedProfId) : undefined,
    status: statusFilter as "pending" | "paid" | "all",
    startDate: new Date(startDate + "T00:00:00-03:00"),
    endDate: new Date(endDate + "T23:59:59-03:00"),
  }), [selectedProfId, statusFilter, startDate, endDate]);

  const { data: report, isLoading, refetch } = trpc.commissions.report.useQuery(
    reportInput,
    { enabled: searched }
  );

  const addAdjustment = trpc.commissions.addAdjustment.useMutation({
    onSuccess: () => {
      toast.success("Ajuste lançado com sucesso!");
      setAdjModal(null);
      setAdjAmount("");
      setAdjDescription("");
      void refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteAdjustment = trpc.commissions.deleteAdjustment.useMutation({
    onSuccess: () => {
      toast.success("Ajuste removido.");
      void refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const [reversalTarget, setReversalTarget] = useState<any | null>(null);
  const [reversalReason, setReversalReason] = useState("");
  const reverseCommissionUsage = trpc.products.reverseCommissionUsage.useMutation({
    onSuccess: (result) => {
      toast.success(`Lançamento estornado. ${result.restoredQty} unidade(s) voltaram ao estoque.`);
      setReversalTarget(null);
      setReversalReason("");
      void refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const [recalcModal, setRecalcModal] = useState(false);
  const [recalcProfId, setRecalcProfId] = useState<string>("all");
  const [forceRecalc, setForceRecalc] = useState(false);

  // Pagamentos de Salário / Vale Transporte
  const [salaryModal, setSalaryModal] = useState(false);
  const [salaryProfId, setSalaryProfId] = useState<string>("");
  const [salaryType, setSalaryType] = useState<"salary" | "transport" | "advance" | "other">("salary");
  const [salaryAmount, setSalaryAmount] = useState("");
  const [salaryDescription, setSalaryDescription] = useState("");
  const [salaryInstallment, setSalaryInstallment] = useState<string>("none");
  const [salaryPaymentDate, setSalaryPaymentDate] = useState(todayStr());

  // Mês de referência para pagamentos de salário (usa o mês do período selecionado)
  const salaryRefMonth = useMemo(() => startDate.slice(0, 7), [startDate]);

  const { data: salaryPayments, refetch: refetchSalary } = trpc.commissions.salaryPayments.list.useQuery(
    { referenceMonth: salaryRefMonth, professionalId: selectedProfId !== "all" ? parseInt(selectedProfId) : undefined },
    { enabled: searched }
  );

  const createSalaryPayment = trpc.commissions.salaryPayments.create.useMutation({
    onSuccess: () => {
      toast.success("Pagamento lançado com sucesso!");
      setSalaryModal(false);
      setSalaryAmount("");
      setSalaryDescription("");
      setSalaryInstallment("none");
      void refetchSalary();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteSalaryPayment = trpc.commissions.salaryPayments.delete.useMutation({
    onSuccess: () => { toast.success("Pagamento removido."); void refetchSalary(); },
    onError: (e) => toast.error(e.message),
  });

  function handleCreateSalaryPayment() {
    if (!salaryProfId) { toast.error("Selecione um profissional."); return; }
    const amt = parseFloat(salaryAmount.replace(",", "."));
    if (isNaN(amt) || amt <= 0) { toast.error("Valor inválido."); return; }
    const typeLabels: Record<string, string> = { salary: "Salário", transport: "Vale Transporte", advance: "Adiantamento", other: "Outro" };
    const installNum = salaryInstallment !== "none" ? parseInt(salaryInstallment) : undefined;
    const installLabel = installNum === 1 ? " - 1ª parcela (dia 15)" : installNum === 2 ? " - 2ª parcela (dia 30)" : "";
    const profName = professionals?.find(p => String(p.id) === salaryProfId)?.name ?? "";
    const desc = salaryDescription.trim() || `${typeLabels[salaryType]}${installLabel} - ${profName} - ${salaryRefMonth}`;
    createSalaryPayment.mutate({
      professionalId: parseInt(salaryProfId),
      type: salaryType,
      amount: amt,
      description: desc,
      referenceMonth: salaryRefMonth,
      installment: installNum,
      paymentDate: new Date(salaryPaymentDate + "T12:00:00-03:00"),
    });
  }

  const recalculate = trpc.commissions.recalculate.useMutation({
    onSuccess: (data) => {
      toast.success(`Recálculo concluído! ${data.generated} comissões geradas, ${data.skipped} sem regra/já existentes.`);
      setRecalcModal(false);
      setForceRecalc(false);
      void refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  function handleRecalculate() {
    recalculate.mutate({
      professionalId: recalcProfId !== "all" ? parseInt(recalcProfId) : undefined,
      startDate: new Date(startDate + "T00:00:00-03:00"),
      endDate: new Date(endDate + "T23:59:59-03:00"),
      force: forceRecalc,
    });
  }

  const markPaid = trpc.commissions.markAllPaidByPeriod.useMutation({
    onSuccess: () => {
      toast.success("Comissões marcadas como pagas!");
      setPayModal(false);
      void refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  function handleSearch() {
    setSearched(true);
    setTimeout(() => void refetch(), 100);
  }

  function handleAddAdjustment() {
    if (!selectedProfId || selectedProfId === "all") {
      toast.error("Selecione um profissional antes de lançar ajuste.");
      return;
    }
    const amt = parseFloat(adjAmount.replace(",", "."));
    if (!amt || amt <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    if (!adjDescription.trim()) {
      toast.error("Informe uma descrição.");
      return;
    }
    addAdjustment.mutate({
      professionalId: parseInt(selectedProfId),
      type: adjModal!.type,
      amount: amt,
      description: adjDescription.trim(),
      referenceDate: new Date(startDate + "T12:00:00-03:00"),
    });
  }

  function handleMarkPaid() {
    if (!selectedProfId || selectedProfId === "all") {
      toast.error("Selecione um profissional para pagar.");
      return;
    }
    markPaid.mutate({
      professionalId: parseInt(selectedProfId),
      startDate: new Date(startDate + "T00:00:00-03:00"),
      endDate: new Date(endDate + "T23:59:59-03:00"),
    });
  }

  const s = report?.summary;
  const rows = report?.rows ?? [];
  const adjustments = report?.adjustments ?? [];
  const productDeductions = report?.productDeductions ?? [];

  const serviceRows = rows.filter((r) => r.sourceType === "service");
  const packageRows = rows.filter((r) => r.sourceType === "package");

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Comissões</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Relatório de comissões por profissional e período
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
          onClick={() => setRecalcModal(true)}
        >
          <CoinsIcon className="w-4 h-4" />
          Recalcular Comissões
        </Button>
      </div>

      {/* Modal de Recálculo Retroativo */}
      <Dialog open={recalcModal} onOpenChange={setRecalcModal}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CoinsIcon className="w-5 h-5 text-amber-600" />
              Recalcular Comissões Retroativamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Gera comissões para agendamentos <strong>concluídos</strong> no período selecionado que ainda não possuem comissão registrada.
            </p>
            <div className="flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-3">
              <input
                type="checkbox"
                id="forceRecalc"
                checked={forceRecalc}
                onChange={e => setForceRecalc(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-rose-600"
              />
              <label htmlFor="forceRecalc" className="text-xs text-rose-800 cursor-pointer">
                <strong>Forçar recalcúlo</strong> — apaga as comissões já existentes no período e recria com a lógica correta. Use para corrigir comissões calculadas incorretamente.
              </label>
            </div>
            <div className="space-y-2">
              <Label>Profissional</Label>
              <Select value={recalcProfId} onValueChange={setRecalcProfId}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os profissionais</SelectItem>
                  {professionals?.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data início</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Data fim</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              ⚠️ Esta operação pode demorar alguns segundos dependendo do volume de agendamentos.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecalcModal(false)}>Cancelar</Button>
            <Button
              onClick={handleRecalculate}
              disabled={recalculate.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {recalculate.isPending ? "Processando..." : "Recalcular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Painel de Regras de Comissão ─────────────────────────────────── */}
      <Card className="border-amber-200 dark:border-amber-800">
        <CardHeader
          className="pb-3 cursor-pointer select-none"
          onClick={() => setRulesOpen((v) => !v)}
        >
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Settings2Icon className="w-4 h-4 text-amber-600" />
              Regras de Comissão
              <Badge variant="secondary" className="text-xs">{commissionRules?.length ?? 0} regras</Badge>
            </span>
            {rulesOpen ? <ChevronUpIcon className="w-4 h-4 text-muted-foreground" /> : <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />}
          </CardTitle>
        </CardHeader>

        {rulesOpen && (
          <CardContent className="space-y-4">
            {/* Formulário de nova regra */}
            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-1">
                <PlusIcon className="w-3 h-3" /> Nova Regra
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Profissional *</Label>
                  <Select value={ruleProf} onValueChange={setRuleProf}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {professionals?.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tipo de Regra *</Label>
                  <Select value={ruleType} onValueChange={(v) => {
                    setRuleType(v as "professional" | "assistant");
                    if (v === "assistant") setRuleServiceIds([]);
                  }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Profissional (por serviço)</SelectItem>
                      <SelectItem value="assistant">Assistente (percentual fixo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs text-muted-foreground">
                      {ruleType === "assistant" ? "Serviços" : "Serviços específicos (opcional)"}
                    </Label>
                    {ruleType === "professional" && (
                      <span className="text-xs text-muted-foreground">
                        {ruleServiceIds.length} selecionado{ruleServiceIds.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {ruleType === "assistant" ? (
                    <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                      A regra de assistente não depende de serviço.
                    </div>
                  ) : (
                    <div className="rounded-md border bg-background p-2">
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-xs text-muted-foreground">Sem seleção, a regra vale para todos os serviços.</span>
                        <div className="flex shrink-0 gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={filteredRuleServices.length === 0}
                            onClick={() => setRuleServiceIds((current) => Array.from(new Set([
                              ...current,
                              ...filteredRuleServices.map((service: any) => service.id),
                            ])))}
                          >
                            {ruleServiceSearch.trim() ? "Todos encontrados" : "Todos"}
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setRuleServiceIds([])}>Limpar</Button>
                        </div>
                      </div>
                      <div className="relative mb-2">
                        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                        <Input
                          aria-label="Buscar serviço para regra de comissão"
                          autoComplete="off"
                          className="h-9 pl-9 pr-9 text-sm"
                          placeholder="Buscar serviço"
                          value={ruleServiceSearch}
                          onChange={(event) => setRuleServiceSearch(event.target.value)}
                        />
                        {ruleServiceSearch && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-9 w-9"
                            onClick={() => setRuleServiceSearch("")}
                            aria-label="Limpar busca de serviço"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <p className="mb-1 text-xs text-muted-foreground" aria-live="polite">
                        {filteredRuleServices.length} serviço{filteredRuleServices.length !== 1 ? "s" : ""} encontrado{filteredRuleServices.length !== 1 ? "s" : ""}
                      </p>
                      <div className="grid max-h-32 grid-cols-1 gap-1 overflow-y-auto pr-1 sm:grid-cols-2" role="group" aria-label="Serviços da regra de comissão">
                        {filteredRuleServices.map((service: any) => {
                          const selected = ruleServiceIds.includes(service.id);
                          return (
                            <label key={service.id} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm transition-colors ${selected ? "border-amber-500/60 bg-amber-50 dark:bg-amber-950/30" : "border-transparent hover:bg-muted"}`}>
                              <Checkbox checked={selected} onCheckedChange={() => toggleRuleService(service.id)} />
                              <span className="min-w-0 truncate">{service.name}</span>
                            </label>
                          );
                        })}
                        {filteredRuleServices.length === 0 && (
                          <p className="col-span-2 py-2 text-center text-xs text-muted-foreground">
                            {adminServices?.length
                              ? "Nenhum serviço encontrado para esta busca."
                              : "Nenhum serviço cadastrado."}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Percentual (%) *</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="Ex: 30"
                      value={rulePercentage}
                      onChange={(e) => setRulePercentage(e.target.value)}
                      className="pr-8"
                    />
                    <PercentIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Salário Fixo Mensal (R$)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Ex: 1500,00"
                      value={ruleFixedSalary}
                      onChange={(e) => setRuleFixedSalary(e.target.value)}
                      disabled={ruleType === "professional" && ruleServiceIds.length > 0}
                    />
                    <Button
                      onClick={handleSaveRule}
                      disabled={upsertRule.isPending || upsertRulesForServices.isPending}
                      className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    >
                      {upsertRule.isPending || upsertRulesForServices.isPending ? "..." : "Salvar"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {ruleType === "professional" && ruleServiceIds.length > 0
                      ? "Disponível somente na regra geral, para não repetir o desconto mensal em cada serviço."
                      : "Deixe em branco se não houver salário fixo."}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {ruleType === "assistant"
                  ? <><strong>Regra de Assistente:</strong> percentual fixo aplicado sobre o valor bruto do atendimento quando este profissional atua como assistente. Não depende do serviço.  <span className="text-blue-600 dark:text-blue-400">Esta regra é usada automaticamente ao selecionar este profissional como assistente no caixa.</span></>
                  : <>Marque um ou mais serviços para criar a mesma porcentagem em cada um. Sem seleção, a regra se aplica a <strong>todos os serviços</strong> do profissional. Regras específicas por serviço têm prioridade sobre a regra geral.</>
                }
              </p>
            </div>

            {/* Listagem de regras existentes */}
            {!commissionRules || commissionRules.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                <PercentIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Nenhuma regra cadastrada. Adicione acima.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Profissional</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Serviço</TableHead>
                        <TableHead className="text-right">Percentual</TableHead>
                        <TableHead className="text-right">Salário Fixo</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissionRules.map((rule: any) => {
                    const prof = professionals?.find((p) => p.id === rule.professionalId);
                    const svc = adminServices?.find((s: any) => s.id === rule.serviceId);
                    const professionalName = rule.professionalName ?? rule.legacyProfessionalName ?? prof?.name ?? "Profissional removido";
                    const professionalAvatarUrl = rule.professionalAvatarUrl ?? prof?.avatarUrl;
                    const isHistoricalRule = !rule.professionalName && !rule.legacyProfessionalName && !prof;
                    return (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {professionalAvatarUrl ? (
                              <img src={professionalAvatarUrl} className="w-6 h-6 rounded-full object-cover" alt="" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                                {professionalName[0] ?? "?"}
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="text-sm font-medium">{professionalName}</span>
                              {isHistoricalRule && (
                                <p className="text-xs text-muted-foreground">Regra histórica sem cadastro ativo</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {rule.ruleType === "assistant" ? (
                            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">Assistente</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs">Profissional</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {rule.ruleType === "assistant" ? (
                            <span className="text-xs text-muted-foreground italic">Percentual fixo</span>
                          ) : svc ? (
                            <Badge variant="outline" className="text-xs">{svc.name}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Todos os serviços</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 font-bold">
                            {parseFloat(rule.percentage).toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {rule.fixedMonthlySalary != null && Number(rule.fixedMonthlySalary) > 0 ? (
                            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 font-bold">
                              {fmt(Number(rule.fixedMonthlySalary))}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                              onClick={() => openEditRule(rule)}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                              onClick={() => deleteRule.mutate({ id: rule.id })}
                              disabled={deleteRule.isPending}
                            >
                              <Trash2Icon className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> Data Início
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> Data Fim
              </Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <UserIcon className="w-3 h-3" /> Profissional
              </Label>
              <Select value={selectedProfId} onValueChange={setSelectedProfId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {professionals?.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Comissões</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="pending">Não pagas</SelectItem>
                  <SelectItem value="paid">Pagas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading ? "Buscando..." : "Buscar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {searched && report && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna principal — tabelas */}
          <div className="lg:col-span-2 space-y-6">

            {/* Tabela de Serviços Avulsos */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ScissorsIcon className="w-4 h-4 text-amber-600" />
                  Serviços Avulsos
                  <Badge variant="secondary">{serviceRows.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {serviceRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhum serviço avulso encontrado.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Profissional</TableHead>
                        <TableHead>Papel</TableHead>
                        <TableHead>Serviço</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Comissão</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {serviceRows.map((r) => (
                        <TableRow key={r.id} className={(r as any).isAssistant ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {r.startTime ? safeFormat(new Date(r.startTime), "dd/MM/yy") : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {r.professionalAvatarUrl ? (
                                <img src={r.professionalAvatarUrl} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                                  {(r.professionalName ?? "?")[0]}
                                </div>
                              )}
                              <span className="text-sm">{r.professionalName ?? "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {(r as any).isAssistant ? (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">Assistente</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs">Principal</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{r.serviceName ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.clientName ?? "—"}</TableCell>
                          <TableCell className="text-right text-sm">{fmt(Number(r.serviceAmount ?? 0))}</TableCell>
                          <TableCell className="text-right text-sm">{Number(r.percentage ?? 0).toFixed(0)}%</TableCell>
                          <TableCell className="text-right text-sm font-medium">{fmt(Number(r.commissionAmount ?? 0))}</TableCell>
                          <TableCell>
                            <Badge variant={r.status === "paid" ? "default" : "outline"} className="text-xs">
                              {r.status === "paid" ? "Pago" : "Pendente"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Pacotes */}
            {packageRows.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PackageIcon className="w-4 h-4 text-purple-600" />
                    Pacotes
                    <Badge variant="secondary">{packageRows.length}</Badge>
                    <span className="text-xs text-muted-foreground font-normal">(preços reduzidos)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Profissional</TableHead>
                        <TableHead>Serviço</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Valor Pacote</TableHead>
                        <TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Comissão</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packageRows.map((r) => (
                        <TableRow key={r.id} className="bg-purple-50/30">
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {r.startTime ? safeFormat(new Date(r.startTime), "dd/MM/yy") : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {r.professionalAvatarUrl ? (
                                <img src={r.professionalAvatarUrl} className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">
                                  {(r.professionalName ?? "?")[0]}
                                </div>
                              )}
                              <span className="text-sm">{r.professionalName ?? "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{r.serviceName ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.clientName ?? "—"}</TableCell>
                          <TableCell className="text-right text-sm">{fmt(Number(r.serviceAmount ?? 0))}</TableCell>
                          <TableCell className="text-right text-sm">{Number(r.percentage ?? 0).toFixed(0)}%</TableCell>
                          <TableCell className="text-right text-sm font-medium">{fmt(Number(r.commissionAmount ?? 0))}</TableCell>
                          <TableCell>
                            <Badge variant={r.status === "paid" ? "default" : "outline"} className="text-xs">
                              {r.status === "paid" ? "Pago" : "Pendente"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Painel lateral — Resumo */}
          <div className="space-y-4">
            <Card className="border-amber-200 bg-amber-50/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-amber-800">Resumo do Período</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="space-y-1.5 pb-3 border-b border-amber-200">
                  <p className="font-semibold text-amber-900 text-xs uppercase tracking-wide">Base de Rateio Geral</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Serviços:</span>
                    <span className="font-medium">{fmt(s?.servicesBase ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pacotes:</span>
                    <span className="font-medium">{fmt(s?.packagesBase ?? 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Total Base:</span>
                    <span>{fmt(s?.baseTotal ?? 0)}</span>
                  </div>
                </div>

                <div className="space-y-1.5 pb-3 border-b border-amber-200">
                  <p className="font-semibold text-amber-900 text-xs uppercase tracking-wide">Rateio (Comissão)</p>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Serviços:</span>
                    <span className="font-medium">{fmt(s?.servicesCommission ?? 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pacotes:</span>
                    <span className="font-medium">{fmt(s?.packagesCommission ?? 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Total Comissão:</span>
                    <span>{fmt(s?.commissionTotal ?? 0)}</span>
                  </div>
                </div>

                <div className="space-y-1.5 pb-3 border-b border-amber-200">
                  <p className="font-semibold text-amber-900 text-xs uppercase tracking-wide">Descontos e Bônus</p>
                  <div className="flex justify-between text-rose-600">
                    <span>Descontos manuais:</span>
                    <span>-{fmt(s?.manualDiscounts ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-rose-600">
                    <span>Consumo de produtos:</span>
                    <span>-{fmt(s?.productDeductionsTotal ?? 0)}</span>
                  </div>
                  {productDeductions.length > 0 && (
                    <div className="ml-3 space-y-0.5 mt-0.5">
                      {productDeductions.map((p) => {
                        const val = Math.abs(Number(p.qty ?? 0)) * Number(p.unitCost ?? 0);
                        return (
                          <div key={`prod-sum-${p.id}`} className="flex justify-between text-xs text-rose-400">
                            <span className="truncate max-w-[160px]">
                              {p.productName ?? "—"} &times; {Math.abs(Number(p.qty ?? 0))} {(p as any).unit ?? "un"}
                            </span>
                            <span className="ml-2 shrink-0">-{fmt(val)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex justify-between text-emerald-600">
                    <span>Bônus:</span>
                    <span>+{fmt(s?.manualBonuses ?? 0)}</span>
                  </div>
                </div>

                <div className="space-y-1.5 pb-3 border-b border-amber-200">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Já pago:</span>
                    <span className="text-emerald-600 font-medium">{fmt(s?.paidTotal ?? 0)}</span>
                  </div>
                </div>

                {(s?.fixedSalaryTotal ?? 0) > 0 && (
                  <div className="flex justify-between text-blue-700 dark:text-blue-400">
                    <span className="flex items-center gap-1">
                      <CoinsIcon className="w-3 h-3" />
                      Salário Fixo Mensal:
                    </span>
                    <span>-{fmt(s?.fixedSalaryTotal ?? 0)}</span>
                  </div>
                )}
                {((s as any)?.attendanceDiscountTotal ?? 0) > 0 && (
                  <div className="flex justify-between text-orange-700 dark:text-orange-400">
                    <span className="flex items-center gap-1">
                      <CoinsIcon className="w-3 h-3" />
                      Desconto Faltas/Atrasos:
                    </span>
                    <span>-{fmt((s as any)?.attendanceDiscountTotal ?? 0)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-base text-amber-900">
                  <span>Total a Pagar:</span>
                  <span>{fmt(s?.totalToPay ?? 0)}</span>
                </div>

                {selectedProfId !== "all" && (s?.pendingTotal ?? 0) > 0 && (
                  <Button
                    className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white gap-2"
                    onClick={() => setPayModal(true)}
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    Pagar Comissão
                  </Button>
                )}

                {selectedProfId === "all" && (
                  <p className="text-xs text-muted-foreground text-center mt-2 flex items-center gap-1 justify-center">
                    <AlertCircleIcon className="w-3 h-3" />
                    Selecione um profissional para pagar
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Descontos e Bônus — abaixo do botão Pagar Comissão */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CoinsIcon className="w-4 h-4 text-rose-500" />
                    Descontos e Bônus
                    <Badge variant="secondary">{adjustments.length + productDeductions.length}</Badge>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-300 text-rose-600 hover:bg-rose-50 gap-1"
                      onClick={() => {
                        if (selectedProfId === "all") {
                          toast.error("Selecione um profissional para lançar desconto.");
                          return;
                        }
                        setAdjModal({ type: "discount" });
                      }}
                    >
                      <TrendingDownIcon className="w-3.5 h-3.5" />
                      Desconto
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 gap-1"
                      onClick={() => {
                        if (selectedProfId === "all") {
                          toast.error("Selecione um profissional para lançar bônus.");
                          return;
                        }
                        setAdjModal({ type: "bonus" });
                      }}
                    >
                      <TrendingUpIcon className="w-3.5 h-3.5" />
                      Bônus
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {adjustments.length === 0 && productDeductions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhum ajuste lançado neste período.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Período</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adjustments.map((a) => (
                        <TableRow key={`adj2-${a.id}`}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {safeFormat(new Date(a.referenceDate), "dd/MM/yy")}
                          </TableCell>
                          <TableCell className="text-sm">{a.description}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={a.type === "discount" ? "border-rose-300 text-rose-600" : "border-emerald-300 text-emerald-600"}
                            >
                              {a.type === "discount" ? "Desconto" : "Bônus"}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right text-sm font-medium ${a.type === "discount" ? "text-rose-600" : "text-emerald-600"}`}>
                            {a.type === "discount" ? "-" : "+"}{fmt(Number(a.amount ?? 0))}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteAdjustment.mutate({ adjustmentId: a.id })}
                            >
                              <Trash2Icon className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {productDeductions.map((p) => {
                        const val = Math.abs(Number(p.qty ?? 0)) * Number(p.unitCost ?? 0);
                        const isOwnUse = (p as any).usageType === "professional_own";
                        const isCheckoutConsumption = (p as any).reason === "consumo_profissional";
                        return (
                          <TableRow key={`prod2-${p.id}`} className="bg-rose-50/30">
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {safeFormat(new Date(p.createdAt), "dd/MM/yy")}
                            </TableCell>
                            <TableCell className="text-sm">
                              <span className="font-medium">{p.productName ?? "—"}</span>
                              <span className="text-muted-foreground ml-1">({Math.abs(Number(p.qty ?? 0))} {(p as any).unit ?? "un"})</span>
                              {(p as any).notes && <p className="text-xs text-muted-foreground mt-0.5">{(p as any).notes}</p>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={isOwnUse ? "border-blue-300 text-blue-600" : "border-amber-300 text-amber-600"}>
                                {isCheckoutConsumption ? "Consumo na comissão" : isOwnUse ? "Uso Próprio" : "Uso Interno"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium text-rose-600">
                              -{fmt(val)}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="icon"
                                variant="ghost"
                                title="Estornar consumo"
                                className="h-7 w-7 text-muted-foreground hover:text-amber-700"
                                onClick={() => {
                                  setReversalTarget(p);
                                  setReversalReason("");
                                }}
                              >
                                <RotateCcwIcon className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ─── Seção de Pagamentos de Salário / Vale Transporte ─────────────────── */}
      {searched && (
        <div className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CoinsIcon className="w-4 h-4 text-blue-500" />
                  Pagamentos de Salário / VT
                  <Badge variant="secondary">{(salaryPayments ?? []).length}</Badge>
                  <span className="text-xs font-normal text-muted-foreground ml-1">Ref: {salaryRefMonth}</span>
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50 gap-1"
                  onClick={() => {
                    setSalaryProfId(selectedProfId !== "all" ? selectedProfId : "");
                    setSalaryModal(true);
                  }}
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Lançar Pagamento
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {(salaryPayments ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Nenhum pagamento lançado para este período.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Profissional</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Parcela</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(salaryPayments ?? []).map((sp) => {
                      const typeLabel: Record<string, string> = { salary: "Salário", transport: "Vale Transp.", advance: "Adiantamento", other: "Outro" };
                      const typeColor: Record<string, string> = { salary: "bg-blue-100 text-blue-700", transport: "bg-green-100 text-green-700", advance: "bg-orange-100 text-orange-700", other: "bg-gray-100 text-gray-700" };
                      return (
                        <TableRow key={sp.id}>
                          <TableCell className="font-medium">{(sp as any).professionalName ?? "-"}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColor[sp.type] ?? ""}`}>
                              {typeLabel[sp.type] ?? sp.type}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{sp.description ?? "-"}</TableCell>
                          <TableCell className="text-sm">
                            {sp.installment === 1 ? "1ª (dia 15)" : sp.installment === 2 ? "2ª (dia 30)" : "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {sp.paymentDate ? new Date(sp.paymentDate).toLocaleDateString("pt-BR") : "-"}
                          </TableCell>
                          <TableCell className="text-right font-medium text-blue-700">{fmt(Number(sp.amount ?? 0))}</TableCell>
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-rose-400 hover:text-rose-600"
                              onClick={() => deleteSalaryPayment.mutate({ id: sp.id })}
                            >
                              <Trash2Icon className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {/* Totais por tipo */}
              {(salaryPayments ?? []).length > 0 && (() => {
                const totals = (salaryPayments ?? []).reduce((acc, sp) => {
                  acc[sp.type] = (acc[sp.type] ?? 0) + Number(sp.amount ?? 0);
                  return acc;
                }, {} as Record<string, number>);
                const typeLabel: Record<string, string> = { salary: "Salário", transport: "Vale Transp.", advance: "Adiantamento", other: "Outro" };
                const total = Object.values(totals).reduce((s, v) => s + v, 0);
                return (
                  <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 justify-end text-sm">
                    {Object.entries(totals).map(([type, val]) => (
                      <span key={type} className="text-muted-foreground">{typeLabel[type] ?? type}: <strong>{fmt(val)}</strong></span>
                    ))}
                    <span className="font-bold text-blue-700">Total: {fmt(total)}</span>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {searched && !report && !isLoading && (
        <div className="text-center py-16 text-muted-foreground">
          <CoinsIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nenhuma comissão encontrada para os filtros selecionados.</p>
        </div>
      )}

      {!searched && (
        <div className="text-center py-16 text-muted-foreground">
          <CoinsIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Selecione o período e clique em <strong>Buscar</strong> para ver as comissões.</p>
        </div>
      )}

      {/* Modal de Lançamento de Salário / Vale Transporte */}
      <Dialog open={salaryModal} onOpenChange={setSalaryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <CoinsIcon className="w-5 h-5" />
              Lançar Pagamento de Salário / VT
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-xs text-blue-700">
              Mês de referência: <strong>{salaryRefMonth}</strong>
            </div>
            <div className="space-y-2">
              <Label>Profissional *</Label>
              <Select value={salaryProfId} onValueChange={setSalaryProfId}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {professionals?.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={salaryType} onValueChange={(v) => setSalaryType(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary">Salário</SelectItem>
                  <SelectItem value="transport">Vale Transporte</SelectItem>
                  <SelectItem value="advance">Adiantamento</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Parcela (pagamento dividido)</Label>
              <Select value={salaryInstallment} onValueChange={setSalaryInstallment}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Pagamento único</SelectItem>
                  <SelectItem value="1">1ª parcela (dia 15)</SelectItem>
                  <SelectItem value="2">2ª parcela (dia 30)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valor (R$) *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0,00"
                value={salaryAmount}
                onChange={(e) => setSalaryAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data do Pagamento *</Label>
              <Input
                type="date"
                value={salaryPaymentDate}
                onChange={(e) => setSalaryPaymentDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                placeholder="Ex: Salário 1ª parcela - julho/2026"
                value={salaryDescription}
                onChange={(e) => setSalaryDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalaryModal(false)}>Cancelar</Button>
            <Button
              onClick={handleCreateSalaryPayment}
              disabled={createSalaryPayment.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {createSalaryPayment.isPending ? "Salvando..." : "Lançar Pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Regra de Comissão */}
      <Dialog open={!!editRuleModal} onOpenChange={(open) => !open && setEditRuleModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <PencilIcon className="w-5 h-5" />
              Editar Regra de Comissão
            </DialogTitle>
          </DialogHeader>
          {editRuleModal && (() => {
            const prof = professionals?.find((p) => p.id === editRuleModal.professionalId);
            return (
              <div className="space-y-4 py-2">
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex items-center gap-2">
                  {prof?.avatarUrl ? (
                    <img src={prof.avatarUrl} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-700">
                      {(prof?.name ?? "?")[0]}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-sm">{prof?.name ?? `#${editRuleModal.professionalId}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {editRuleModal.serviceId
                        ? adminServices?.find((s: any) => s.id === editRuleModal.serviceId)?.name ?? `Serviço #${editRuleModal.serviceId}`
                        : "Todos os serviços"}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tipo de Regra</Label>
                  <Select value={editRuleType} onValueChange={(v) => setEditRuleType(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Profissional</SelectItem>
                      <SelectItem value="assistant">Assistente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editRuleType !== "assistant" && (
                  <div className="space-y-2">
                    <Label>Serviço</Label>
                    <Select value={editRuleService} onValueChange={setEditRuleService}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os serviços</SelectItem>
                        {adminServices?.map((s: any) => (
                          <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Percentual de Comissão (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="Ex: 50"
                    value={editRulePercentage}
                    onChange={(e) => setEditRulePercentage(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Salário Fixo Mensal (R$) <span className="text-muted-foreground text-xs">opcional</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Ex: 1500,00"
                    value={editRuleFixedSalary}
                    onChange={(e) => setEditRuleFixedSalary(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Deixe em branco se não houver salário fixo.</p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRuleModal(null)}>Cancelar</Button>
            <Button
              onClick={handleUpdateRule}
              disabled={upsertRule.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {upsertRule.isPending ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Desconto / Bônus */}
      <Dialog open={!!adjModal} onOpenChange={() => setAdjModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className={adjModal?.type === "discount" ? "text-rose-600" : "text-emerald-600"}>
              {adjModal?.type === "discount" ? "Lançar Desconto" : "Lançar Bônus"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Input
                placeholder={adjModal?.type === "discount" ? "Ex: Gastos com Produtos" : "Ex: Bônus por meta atingida"}
                value={adjDescription}
                onChange={(e) => setAdjDescription(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0,00"
                value={adjAmount}
                onChange={(e) => setAdjAmount(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjModal(null)}>Cancelar</Button>
            <Button
              onClick={handleAddAdjustment}
              disabled={addAdjustment.isPending}
              className={adjModal?.type === "discount" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            >
              {addAdjustment.isPending ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estorno auditável de consumo na comissão — somente AdminCommissions */}
      <Dialog open={!!reversalTarget} onOpenChange={(open) => !open && setReversalTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcwIcon className="h-5 w-5" />
              Estornar consumo na comissão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              O desconto deixará de contar na comissão e a quantidade retornará ao estoque. O lançamento original continuará no histórico como estornado.
            </div>
            {reversalTarget && (
              <div className="rounded-lg border p-3 text-sm">
                <p className="font-semibold">{reversalTarget.productName ?? "Produto"}</p>
                <p className="text-muted-foreground">
                  {Math.abs(Number(reversalTarget.qty ?? 0))} {reversalTarget.unit ?? "un"} · {fmt(Math.abs(Number(reversalTarget.qty ?? 0)) * Number(reversalTarget.unitCost ?? 0))}
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Motivo do estorno</Label>
              <Input
                value={reversalReason}
                onChange={(event) => setReversalReason(event.target.value)}
                placeholder="Ex: lançamento feito por engano"
                maxLength={255}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReversalTarget(null)}>Cancelar</Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              disabled={reversalReason.trim().length < 3 || reverseCommissionUsage.isPending}
              onClick={() => reversalTarget && reverseCommissionUsage.mutate({
                movementId: Number(reversalTarget.id),
                reason: reversalReason.trim(),
              })}
            >
              {reverseCommissionUsage.isPending ? "Estornando..." : "Confirmar estorno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmar pagamento */}
      <Dialog open={payModal} onOpenChange={setPayModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar Pagamento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Marcar todas as comissões pendentes do período como <strong>pagas</strong>?
            Valor total a pagar: <strong className="text-amber-700">{fmt(s?.totalToPay ?? 0)}</strong>
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayModal(false)}>Cancelar</Button>
            <Button
              onClick={handleMarkPaid}
              disabled={markPaid.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {markPaid.isPending ? "Processando..." : "Confirmar Pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
